import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCredential,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  inMemoryPersistence
} from 'firebase/auth';
import { auth, googleProvider, appleProvider } from '../firebase';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { Capacitor } from '@capacitor/core';
import { createOrUpdateUser } from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState(null);

  console.log('🔐 AuthProvider-simple initialized');

  async function signInWithGoogle() {
    console.log('🔐 Starting Google Sign-In');
    if (Capacitor.isNativePlatform()) {
      try {
        const result = await SocialLogin.login({
          provider: 'google',
          options: {
            scopes: ['profile', 'email']
          }
        });
        console.log('🔐 Google Sign-In completed');
        
        // Convert Google ID token to Firebase credential
        const credential = GoogleAuthProvider.credential(result.result.idToken);
        
        // Check if we're on iOS (where Firebase signInWithCredential fails)
        const isIOS = Capacitor.getPlatform() === 'ios';
        
        if (isIOS) {
          // Skip Firebase signInWithCredential on iOS - it consistently fails/hangs
          console.log('🔐 Using iOS fallback authentication');
          
          // Fallback: manually set user state and use Google ID token
          const firebaseUser = {
            uid: result.result.profile.id,
            email: result.result.profile.email,
            displayName: result.result.profile.name,
            photoURL: result.result.profile.imageUrl,
            getIdToken: async (forceRefresh = false) => {
              // If force refresh is requested, re-authenticate with Google
              if (forceRefresh) {
                console.log('🔐 Force refresh requested, re-authenticating with Google...');
                try {
                  const newResult = await SocialLogin.login({
                    provider: 'google',
                    options: {
                      scopes: ['profile', 'email']
                    }
                  });
                  
                  // Update stored auth data with new token
                  const newAuthData = {
                    uid: newResult.result.profile.id,
                    email: newResult.result.profile.email,
                    displayName: newResult.result.profile.name,
                    photoURL: newResult.result.profile.imageUrl,
                    idToken: newResult.result.idToken,
                    timestamp: Date.now()
                  };
                  localStorage.setItem('tulidu_auth', JSON.stringify(newAuthData));
                  
                  console.log('🔐 Successfully refreshed Google ID token');
                  return newResult.result.idToken;
                } catch (error) {
                  console.error('🔐 Failed to refresh Google ID token:', error);
                  throw error;
                }
              }
              return result.result.idToken; // Return original Google ID token
            },
          };
          
          // Persist authentication data for app/page reload persistence
          const authData = {
            uid: result.result.profile.id,
            email: result.result.profile.email,
            displayName: result.result.profile.name,
            photoURL: result.result.profile.imageUrl,
            idToken: result.result.idToken,
            timestamp: Date.now()
          };
          localStorage.setItem('tulidu_auth', JSON.stringify(authData));
          
          // Create or update user in database
          try {
            const nameParts = result.result.profile.name ? result.result.profile.name.split(' ') : [];
            const userData = {
              email: result.result.profile.email,
              first_name: nameParts[0] || null,
              last_name: nameParts.slice(1).join(' ') || null
            };
            await createOrUpdateUser(userData);
            console.log('🔐 User created/updated in database successfully (iOS)');
          } catch (userError) {
            console.error('🔐 Error creating/updating user in database (iOS):', userError);
            // Don't fail authentication if user creation fails
          }
          
          setCurrentUser(firebaseUser);
          setIdToken(result.result.idToken);
          setLoading(false);
          
          return { user: firebaseUser };
        } else {
          // Android: Use Firebase signInWithCredential (this should work properly)
          console.log('🔐 Using Firebase authentication on Android');
          const firebaseResult = await signInWithCredential(auth, credential);
          console.log('🔐 Firebase authentication successful:', firebaseResult.user.email);
          return firebaseResult;
        }
      } catch (error) {
        console.error('🔐 Error signing in with Google:', error);
        console.error('🔐 Error details:', error.message, error.code);
        throw error;
      }
    } else {
      return signInWithPopup(auth, googleProvider);
    }
  }

  async function signInWithApple() {
    console.log('🔐 Starting Apple Sign-In');
    console.log('🔐 Platform:', Capacitor.getPlatform());
    
    if (Capacitor.isNativePlatform()) {
      const platform = Capacitor.getPlatform();
      
      if (platform === 'ios') {
        // For iOS, use SocialLogin plugin
        try {
          const result = await SocialLogin.login({
            provider: 'apple',
            options: {
              scopes: ['email', 'name']
            }
          });
          console.log('🔐 Apple Sign-In completed on iOS');
          
          // Create a mock Firebase user object
          const firebaseUser = {
            uid: result.result.profile.user,
            email: result.result.profile.email,
            displayName: `${result.result.profile.givenName} ${result.result.profile.familyName}`.trim() || result.result.profile.email,
            photoURL: null,
            getIdToken: async (forceRefresh = false) => {
              // For Apple Sign-In, we don't support token refresh
              // Return the original token or throw an error
              if (forceRefresh) {
                console.log('🔐 Apple Sign-In token refresh not supported, user needs to sign in again');
                throw new Error('Apple Sign-In token refresh not supported');
              }
              return result.result.idToken;
            },
          };
          
          // Store authentication data for persistence
          const authData = {
            uid: result.result.profile.user,
            email: result.result.profile.email,
            displayName: `${result.result.profile.givenName} ${result.result.profile.familyName}`.trim() || result.result.profile.email,
            photoURL: null,
            idToken: result.result.idToken,
            timestamp: Date.now()
          };
          localStorage.setItem('tulidu_auth', JSON.stringify(authData));
          
          // Set user state
          setCurrentUser(firebaseUser);
          setIdToken(result.result.idToken);
          
          // Create or update user in backend
          const userData = {
            uid: result.result.profile.user,
            email: result.result.profile.email,
            displayName: `${result.result.profile.givenName} ${result.result.profile.familyName}`.trim() || result.result.profile.email,
            photoURL: null,
            idToken: result.result.idToken
          };
          await createOrUpdateUser(userData);
          
          return firebaseUser;
        } catch (error) {
          console.error('🔐 Error signing in with Apple on iOS:', error);
          console.error('🔐 Error details:', error.message, error.code);
          throw error;
        }
      } else if (platform === 'android') {
        // For Android, Apple Sign-In is not well supported by SocialLogin plugin
        // Show a helpful message to the user
        throw new Error('Apple Sign-In is not available on Android devices. Please use Google Sign-In or Email/Password authentication instead.');
      }
    } else {
      // Web platform - use Firebase popup
      try {
        const result = await signInWithPopup(auth, appleProvider);
        console.log('🔐 Apple Sign-In successful on web');
        
        // Create or update user in backend
        await createOrUpdateUser(result.user, await result.user.getIdToken());
        
        return result.user;
      } catch (error) {
        console.error('🔐 Error signing in with Apple on web:', error);
        throw error;
      }
    }
  }

  async function logout() {
    console.log('🔐 logout called');
    if (Capacitor.isNativePlatform()) {
      await SocialLogin.logout({ provider: 'google' });
    }
    
    // Clear persisted authentication data
    localStorage.removeItem('tulidu_auth');
    
    // Clear AuthContext state immediately
    setCurrentUser(null);
    setIdToken(null);
    
    // Then sign out from Firebase
    await signOut(auth);
    
    console.log('🔐 Logout completed, user state cleared');
  }

  async function deleteAccount() {
    console.log('🔐 deleteAccount called');
    try {
      // Import the API function
      const { deleteUserAccount } = await import('../services/api');
      
      // Delete user data from backend
      await deleteUserAccount();
      
      // Clear persisted authentication data
      localStorage.removeItem('tulidu_auth');
      
      // Clear AuthContext state immediately
      setCurrentUser(null);
      setIdToken(null);
      
      // Delete the Firebase user account
      if (auth.currentUser) {
        await auth.currentUser.delete();
      }
      
      console.log('🔐 Account deletion completed');
      return { success: true };
    } catch (error) {
      console.error('🔐 Error deleting account:', error);
      throw error;
    }
  }

  // Email/Password Authentication Methods
  async function signUpWithEmail(email, password, displayName) {
    console.log('🔐 Starting email sign-up');
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      console.log('🔐 Firebase email sign-up successful');
      
      // Update the user's display name
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }
      
      // Create or update user in database
      try {
        const nameParts = displayName ? displayName.split(' ') : [];
        const userData = {
          uid: result.user.uid,
          email: email,
          first_name: nameParts[0] || null,
          last_name: nameParts.slice(1).join(' ') || null,
          idToken: await result.user.getIdToken()
        };
        await createOrUpdateUser(userData);
        console.log('🔐 User created/updated in database successfully');
      } catch (userError) {
        console.error('🔐 Error creating/updating user in database:', userError);
        // Don't fail authentication if user creation fails
      }
      
      console.log('🔐 Email sign-up completed');
      return result;
    } catch (error) {
      console.error('🔐 Error signing up with email:', error);
      throw error;
    }
  }

  async function signInWithEmail(email, password) {
    console.log('🔐 Starting email sign-in');
    console.log('🔐 Platform:', Capacitor.getPlatform());
    console.log('🔐 Email:', email);
    
    try {
      console.log('🔐 Using indexedDB persistence initialization for iOS...');
      
      console.log('🔐 Calling signInWithEmailAndPassword with timeout wrapper...');
      
      // Wrap authentication in a timeout to prevent infinite hanging
      const authPromise = signInWithEmailAndPassword(auth, email, password);
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Authentication timeout after 15 seconds')), 15000);
      });
      
      const result = await Promise.race([authPromise, timeoutPromise]);
      
      console.log('🔐 Firebase email authentication successful');
      console.log('🔐 Firebase user:', result.user.uid, result.user.email);
      
      // Create or update user in backend
      try {
        console.log('🔐 Getting ID token...');
        const idToken = await result.user.getIdToken();
        console.log('🔐 ID token obtained, length:', idToken.length);
        
        const nameParts = result.user.displayName ? result.user.displayName.split(' ') : [];
        const userData = {
          uid: result.user.uid,
          email: result.user.email,
          first_name: nameParts[0] || null,
          last_name: nameParts.slice(1).join(' ') || null,
          idToken: idToken
        };
        
        console.log('🔐 Calling createOrUpdateUser...');
        await createOrUpdateUser(userData);
        console.log('🔐 User synced with backend successfully');
      } catch (userError) {
        console.error('🔐 Error syncing user with backend:', userError);
        console.error('🔐 User error details:', userError.message, userError.stack);
        // Don't fail authentication if user sync fails
      }
      
      console.log('🔐 Email sign-in completed successfully');
      return result;
    } catch (error) {
      console.error('🔐 Error signing in with email:', error);
      console.error('🔐 Error details:', error.message, error.code, error.stack);
      throw error;
    }
  }

  async function resetPassword(email) {
    console.log('🔐 Starting password reset');
    try {
      await sendPasswordResetEmail(auth, email);
      console.log('🔐 Password reset email sent');
    } catch (error) {
      console.error('🔐 Error sending password reset email:', error);
      throw error;
    }
  }

  useEffect(() => {
    console.log('🔐 Setting up onAuthStateChanged listener');
    
    // Add debugging for Firebase Auth state changes
    const debugAuthState = (user) => {
      if (user) {
        console.log('🔐 Firebase Auth State: User signed in', {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          emailVerified: user.emailVerified
        });
      } else {
        console.log('🔐 Firebase Auth State: User signed out');
      }
    };
    
    // Check for persisted authentication state first
    const checkPersistedAuth = async () => {
      const persistedAuth = localStorage.getItem('tulidu_auth');
      if (persistedAuth) {
        try {
          const authData = JSON.parse(persistedAuth);
          console.log('🔐 Found persisted auth data:', authData.email);
          
          // Check if the persisted data is not too old (24 hours)
          const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
          if (authData.timestamp && (Date.now() - authData.timestamp) > maxAge) {
            console.log('🔐 Persisted auth data is too old, clearing it');
            localStorage.removeItem('tulidu_auth');
            return;
          }
          
          // Check if the Google ID token is likely expired (Google ID tokens expire in 1 hour)
          const tokenAge = Date.now() - authData.timestamp;
          const tokenMaxAge = 50 * 60 * 1000; // 50 minutes (10 minutes before 1-hour expiration)
          if (tokenAge > tokenMaxAge) {
            console.log('🔐 Persisted Google ID token is likely expired, clearing it');
            localStorage.removeItem('tulidu_auth');
            return;
          }
          
          // Create a mock user object for fallback authentication
          const mockUser = {
            uid: authData.uid,
            email: authData.email,
            displayName: authData.displayName,
            photoURL: authData.photoURL,
            getIdToken: async (forceRefresh = false) => {
              // If force refresh is requested, re-authenticate with Google
              if (forceRefresh) {
                console.log('🔐 Force refresh requested for persisted auth, re-authenticating with Google...');
                try {
                  const newResult = await SocialLogin.login({
                    provider: 'google',
                    options: {
                      scopes: ['profile', 'email']
                    }
                  });
                  
                  // Update stored auth data with new token
                  const newAuthData = {
                    uid: newResult.result.profile.id,
                    email: newResult.result.profile.email,
                    displayName: newResult.result.profile.name,
                    photoURL: newResult.result.profile.imageUrl,
                    idToken: newResult.result.idToken,
                    timestamp: Date.now()
                  };
                  localStorage.setItem('tulidu_auth', JSON.stringify(newAuthData));
                  
                  // Update the current user state
                  setCurrentUser({
                    ...mockUser,
                    getIdToken: async (forceRefresh = false) => {
                      if (forceRefresh) {
                        return newResult.result.idToken;
                      }
                      return newResult.result.idToken;
                    }
                  });
                  setIdToken(newResult.result.idToken);
                  
                  console.log('🔐 Successfully refreshed Google ID token for persisted auth');
                  return newResult.result.idToken;
                } catch (error) {
                  console.error('🔐 Failed to refresh Google ID token for persisted auth:', error);
                  throw error;
                }
              }
              return authData.idToken;
            },
          };
          
          // Create or update user in database for persisted auth
          try {
            const nameParts = authData.displayName ? authData.displayName.split(' ') : [];
            const userData = {
              email: authData.email,
              first_name: nameParts[0] || null,
              last_name: nameParts.slice(1).join(' ') || null
            };
            await createOrUpdateUser(userData);
            console.log('🔐 User created/updated in database successfully (persisted auth)');
          } catch (userError) {
            console.error('🔐 Error creating/updating user in database (persisted auth):', userError);
            // Don't fail authentication if user creation fails
          }
          
          setCurrentUser(mockUser);
          setIdToken(authData.idToken);
          setLoading(false);
          
          console.log('🔐 Restored authentication from localStorage');
          return;
        } catch (error) {
          console.error('🔐 Error parsing persisted auth:', error);
          localStorage.removeItem('tulidu_auth');
        }
      }
    };
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 Auth state changed:', user ? user.email : 'null');
      
      // Debug Firebase Auth state
      debugAuthState(user);
      
      setCurrentUser(user);
      
      // Get Firebase ID token for API requests
      if (user) {
        try {
          // Get Firebase ID token (don't force refresh to avoid quota limits)
          const token = await user.getIdToken(false);
          setIdToken(token);
          
          // Create or update user in database
          try {
            const nameParts = user.displayName ? user.displayName.split(' ') : [];
            const userData = {
              email: user.email,
              first_name: nameParts[0] || null,
              last_name: nameParts.slice(1).join(' ') || null
            };
            await createOrUpdateUser(userData);
            console.log('🔐 User created/updated in database successfully');
          } catch (userError) {
            console.error('🔐 Error creating/updating user in database:', userError);
            // Don't fail authentication if user creation fails
          }
          
          // Persist Firebase authentication data
          const authData = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            idToken: token,
            timestamp: Date.now()
          };
          localStorage.setItem('tulidu_auth', JSON.stringify(authData));
        } catch (error) {
          console.error('🔐 Error getting Firebase ID token:', error);
        }
      } else {
        setIdToken(null);
        // Clear persisted auth if Firebase user is null
        localStorage.removeItem('tulidu_auth');
      }
      
      setLoading(false);
      
      console.log('🔐 Auth state updated, currentUser set to:', user ? user.email : 'null');
    });

    // Check for persisted auth before setting up the listener
    checkPersistedAuth();

    // Listen for token expiration events from API interceptor
    const handleTokenExpired = () => {
      console.log('🔐 Received auth-token-expired event, clearing auth state');
      setCurrentUser(null);
      setIdToken(null);
      localStorage.removeItem('tulidu_auth');
    };

    window.addEventListener('auth-token-expired', handleTokenExpired);

    return () => {
      unsubscribe();
      window.removeEventListener('auth-token-expired', handleTokenExpired);
    };
  }, []);

          const value = {
            currentUser,
            idToken,
            signInWithGoogle,
            signInWithApple,
            signUpWithEmail,
            signInWithEmail,
            resetPassword,
            logout,
            deleteAccount
          };

          // Expose AuthContext to global scope for API interceptor
          if (typeof window !== 'undefined') {
            window.__TULIDU_AUTH_CONTEXT__ = value;
          }

          console.log('🔐 AuthProvider value created:', { 
            currentUser: currentUser ? currentUser.email : null, 
            loading 
          });

          return (
            <AuthContext.Provider value={value}>
              {children}
            </AuthContext.Provider>
          );
}
